import { useEffect, useState } from "react";
import api from "@/lib/api";
import { usePlayer } from "@/context/PlayerContext";
import { toast } from "sonner";
import {
  Sparkles,
  Check,
  Anchor,
  Flame,
  Wind,
  Infinity as InfinityIcon,
  Heart,
  Users,
  Lightbulb,
  Sun,
  Shuffle,
  Lock,
  Music,
  Star,
} from "lucide-react";

const LS_KEY = "bbm_healing_hz";

const SPECIAL_TRACKS = [
  {
    id: "handpan",
    label: "Calming Handpan",
    tagline: "Percussive Meditation",
    description:
      "Slow, hand-struck steel drum tones. Ideal for deep breathing, meditation, or falling asleep — a warmer alternative to pure sine frequencies.",
    Icon: Music,
  },
  {
    id: "om",
    label: "Om Meditation",
    tagline: "Sacred Chant",
    description:
      "The primal Om chant — considered the sound of the universe. Steadies the breath, calms the mind, and anchors deep meditation or restorative sleep.",
    Icon: Sparkles,
  },
];

// Per-frequency icon + long-form description.
const FREQ_META = {
  174: {
    Icon: Anchor,
    description:
      "The lowest of the solfeggio tones. Believed to reduce physical and emotional pain, ease stress, and provide a sense of security and grounding. Perfect for deep relaxation before sleep.",
  },
  396: {
    Icon: Flame,
    description:
      "Liberates you from lingering guilt, fear, and negative emotions. Helps clear subconscious blocks so you can rest without carrying the weight of the day into your dreams.",
  },
  417: {
    Icon: Wind,
    description:
      "Cleanses traumatic experiences and destructive influences. Encourages positive change, opens the mind, and helps break unproductive patterns while restoring balance.",
  },
  432: {
    Icon: InfinityIcon,
    description:
      "The natural tuning of the universe — mathematically aligned with the earth's frequency. Promotes deep calm, spiritual expansion, and a feeling of oneness with nature.",
  },
  528: {
    Icon: Heart,
    description:
      "The Love Frequency, sometimes called the Miracle Tone. Associated with DNA repair, transformation, and inner peace. Elevates self-love and cellular healing.",
  },
  639: {
    Icon: Users,
    description:
      "Enhances communication, understanding, tolerance, and love. Helps repair relationships, resolve conflicts, and strengthen bonds — both with others and with yourself.",
  },
  741: {
    Icon: Lightbulb,
    description:
      "Aids self-expression, problem-solving, and detoxification. Promotes clarity of thought and emotional honesty; ideal for creative unblocking and mental reset.",
  },
  963: {
    Icon: Sun,
    description:
      "The Frequency of the Gods. Reconnects you to source energy and universal consciousness. Awakens intuition and promotes higher states of spiritual awareness.",
  },
  1111: {
    Icon: Star,
    description:
      "The Angel Number frequency. Associated with spiritual awakening, alignment with the universe, and manifestation. Signals a wake-up call to trust your intuition and set intentions.",
  },
};

const FrequencyPicker = () => {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(() => localStorage.getItem(LS_KEY) || "auto");
  const { nowPlaying, playFrequency, clearFrequencyOverride, setPreferredHz } = usePlayer();

  useEffect(() => {
    api.get("/healing-frequencies").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const choose = (val) => {
    setSelected(val);
    const parsed =
      val === "auto" ? null : /^\d+$/.test(val) ? parseInt(val, 10) : val;

    if (val === "auto") {
      localStorage.removeItem(LS_KEY);
    } else {
      localStorage.setItem(LS_KEY, String(val));
    }

    if (val === "auto") {
      // Clear any active override and restore auto-rotation for tonight.
      clearFrequencyOverride?.();
      setPreferredHz(null);
      toast("Auto rotation restored", {
        description: "Tonight's Overnight Healing Session will cycle through frequencies.",
        icon: <Lock size={14} />,
      });
      return;
    }

    // Play the selected frequency in the persistent player NOW (regardless of the current
    // block/schedule) and also persist it as tonight's preferred frequency for the
    // automated 00:00–05:30 ET Overnight Healing rotation.
    playFrequency(parsed);
    const label = /^\d+$/.test(val)
      ? `${val} Hz`
      : (SPECIAL_TRACKS.find((s) => s.id === val)?.label || val);
    toast(`${label} — playing now`, {
      description: "Also locked for tonight's Overnight Healing Session (12:00 AM ET).",
      icon: <Lock size={14} />,
    });
  };

  if (!data) return null;

  const defaultHz = data.default_hz;

  return (
    <section id="frequency" className="border-b border-white/5">
      <div className="mx-auto max-w-[1400px] px-4 py-20 sm:px-6">
        <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
          <div>
            <div className="font-mono text-base font-bold uppercase tracking-[0.28em] text-[#A855F7]">§ 02</div>
            <h2 className="mt-2 font-[Unbounded,sans-serif] text-3xl font-bold uppercase tracking-tight sm:text-4xl">
              Overnight Healing Sessions
            </h2>
            <p className="mt-3 max-w-xl font-mono text-base font-bold text-[#FAFAFA]">
              Between <span className="text-[#A855F7]">12:00 AM — 5:30 AM</span> the station switches to ambient
              solfeggio frequencies. Pick one to lock in for tonight, or let the nightly rotation choose for you.
            </p>
          </div>
          <div className="flex items-center gap-2 border border-white/10 bg-black/50 px-3 py-2 font-mono text-sm font-bold uppercase tracking-[0.22em] backdrop-blur-sm">
            <Sparkles size={12} className="text-[#A855F7]" />
            {data.in_block ? (
              <span className="text-[#FAFAFA]">Playing now · {nowPlaying?.title || `${defaultHz} Hz`}</span>
            ) : (
              <span className="text-[#A1A1AA]">Tonight&apos;s default · {defaultHz} Hz</span>
            )}
          </div>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-px bg-white/5 sm:grid-cols-2 lg:grid-cols-3">
          {/* Auto / rotation card */}
          <button
            type="button"
            onClick={() => choose("auto")}
            data-testid="freq-choice-auto"
            className={`flex flex-col items-start gap-3 bg-black/55 p-6 text-left backdrop-blur-sm transition-colors hover:bg-black/75 ${
              selected === "auto" ? "outline outline-2 -outline-offset-2 outline-[#A855F7]" : ""
            }`}
          >
            <div className="flex w-full items-center justify-between">
              <div className="flex h-10 w-10 items-center justify-center border border-[#A855F7]/40 text-[#A855F7]">
                <Shuffle size={18} strokeWidth={2.25} />
              </div>
              <span className="font-mono text-sm font-bold uppercase tracking-[0.22em] text-[#A855F7]">
                Nightly
              </span>
              {selected === "auto" ? <Check size={16} className="text-[#A855F7]" /> : null}
            </div>
            <div className="font-[Unbounded,sans-serif] text-2xl font-bold text-[#FAFAFA]">Auto</div>
            <div className="font-mono text-sm font-bold uppercase tracking-[0.18em] text-[#A1A1AA]">
              Rotates nightly
            </div>
            <p className="mt-1 font-mono text-base font-bold leading-relaxed text-[#FAFAFA]">
              Let the station cycle through all frequencies across the week. Tonight&apos;s default is{" "}
              <span className="text-[#A855F7]">{defaultHz} Hz</span>.
            </p>
          </button>

          {data.options.map((opt) => {
            const active = selected === String(opt.hz);
            const meta = FREQ_META[opt.hz] || {};
            const Icon = meta.Icon || Sparkles;
            return (
              <button
                key={opt.hz}
                type="button"
                onClick={() => choose(String(opt.hz))}
                data-testid={`freq-choice-${opt.hz}`}
                className={`flex flex-col items-start gap-3 bg-black/55 p-6 text-left backdrop-blur-sm transition-colors hover:bg-black/75 ${
                  active ? "outline outline-2 -outline-offset-2 outline-[#A855F7]" : ""
                }`}
              >
                <div className="flex w-full items-center justify-between">
                  <div
                    className={`flex h-10 w-10 items-center justify-center border ${
                      active ? "border-[#A855F7] text-[#A855F7]" : "border-white/20 text-[#FAFAFA]"
                    }`}
                  >
                    <Icon size={18} strokeWidth={2.25} />
                  </div>
                  <span className="font-mono text-sm font-bold uppercase tracking-[0.22em] text-[#A855F7]">
                    {opt.hz === defaultHz ? "Default" : "Alt"}
                  </span>
                  {active ? <Check size={16} className="text-[#A855F7]" /> : null}
                </div>
                <div className="font-[Unbounded,sans-serif] text-3xl font-bold text-[#FAFAFA]">
                  {opt.hz} Hz
                </div>
                <div className="font-mono text-base font-bold uppercase tracking-[0.16em] text-[#A855F7]">
                  {opt.meaning}
                </div>
                <p className="mt-1 font-mono text-base font-bold leading-relaxed text-[#FAFAFA]">
                  {meta.description || ""}
                </p>
              </button>
            );
          })}

          {/* Special / non-Hz tracks (Handpan, etc.) */}
          {SPECIAL_TRACKS.map((sp) => {
            const active = selected === sp.id;
            const Icon = sp.Icon;
            return (
              <button
                key={sp.id}
                type="button"
                onClick={() => choose(sp.id)}
                data-testid={`freq-choice-${sp.id}`}
                className={`flex flex-col items-start gap-3 bg-black/55 p-6 text-left backdrop-blur-sm transition-colors hover:bg-black/75 ${
                  active ? "outline outline-2 -outline-offset-2 outline-[#A855F7]" : ""
                }`}
              >
                <div className="flex w-full items-center justify-between">
                  <div
                    className={`flex h-10 w-10 items-center justify-center border ${
                      active ? "border-[#A855F7] text-[#A855F7]" : "border-white/20 text-[#FAFAFA]"
                    }`}
                  >
                    <Icon size={18} strokeWidth={2.25} />
                  </div>
                  <span className="font-mono text-sm font-bold uppercase tracking-[0.22em] text-[#A855F7]">
                    Special
                  </span>
                  {active ? <Check size={16} className="text-[#A855F7]" /> : null}
                </div>
                <div className="font-[Unbounded,sans-serif] text-2xl font-bold text-[#FAFAFA]">
                  {sp.label}
                </div>
                <div className="font-mono text-base font-bold uppercase tracking-[0.16em] text-[#A855F7]">
                  {sp.tagline}
                </div>
                <p className="mt-1 font-mono text-base font-bold leading-relaxed text-[#FAFAFA]">
                  {sp.description}
                </p>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FrequencyPicker;
